"use strict";
import { useEffect } from "react";
import { writeContract, waitForTransactionReceipt, switchChain, getAccount } from "@wagmi/core";
import { getAddress as checksumAddress } from "viem";
import { wagmiConfig } from "components/Web3Provider/wagmiConfig";
const ERC20_ABI = [
  {
    inputs: [
      { internalType: "address", name: "spender", type: "address" },
      { internalType: "uint256", name: "amount", type: "uint256" }
    ],
    name: "approve",
    outputs: [{ internalType: "bool", name: "", type: "bool" }],
    stateMutability: "nonpayable",
    type: "function"
  }
];
const VOTING_ESCROW_ABI = [
  {
    inputs: [
      { internalType: "address", name: "operator", type: "address" },
      { internalType: "bool", name: "approved", type: "bool" }
    ],
    name: "setApprovalForAll",
    outputs: [],
    stateMutability: "nonpayable",
    type: "function"
  }
];
const SWAP_ROUTER_ABI = [
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "tokenIn", type: "address" },
          { internalType: "address", name: "tokenOut", type: "address" },
          { internalType: "int24", name: "tickSpacing", type: "int24" },
          { internalType: "address", name: "recipient", type: "address" },
          { internalType: "uint256", name: "deadline", type: "uint256" },
          { internalType: "uint256", name: "amountIn", type: "uint256" },
          { internalType: "uint256", name: "amountOutMinimum", type: "uint256" },
          { internalType: "uint160", name: "sqrtPriceLimitX96", type: "uint160" }
        ],
        internalType: "struct ISwapRouter.ExactInputSingleParams",
        name: "params",
        type: "tuple"
      }
    ],
    name: "exactInputSingle",
    outputs: [{ internalType: "uint256", name: "amountOut", type: "uint256" }],
    stateMutability: "payable",
    type: "function"
  }
];
const NFT_POSITION_MANAGER_ABI = [
  {
    inputs: [
      {
        components: [
          { internalType: "address", name: "token0", type: "address" },
          { internalType: "address", name: "token1", type: "address" },
          { internalType: "int24", name: "tickSpacing", type: "int24" },
          { internalType: "int24", name: "tickLower", type: "int24" },
          { internalType: "int24", name: "tickUpper", type: "int24" },
          { internalType: "uint256", name: "amount0Desired", type: "uint256" },
          { internalType: "uint256", name: "amount1Desired", type: "uint256" },
          { internalType: "uint256", name: "amount0Min", type: "uint256" },
          { internalType: "uint256", name: "amount1Min", type: "uint256" },
          { internalType: "address", name: "recipient", type: "address" },
          { internalType: "uint256", name: "deadline", type: "uint256" },
          { internalType: "uint160", name: "sqrtPriceX96", type: "uint160" }
        ],
        internalType: "struct INonfungiblePositionManager.MintParams",
        name: "params",
        type: "tuple"
      }
    ],
    name: "mint",
    outputs: [
      { internalType: "uint256", name: "tokenId", type: "uint256" },
      { internalType: "uint128", name: "liquidity", type: "uint128" },
      { internalType: "uint256", name: "amount0", type: "uint256" },
      { internalType: "uint256", name: "amount1", type: "uint256" }
    ],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [
      {
        components: [
          { internalType: "uint256", name: "tokenId", type: "uint256" },
          { internalType: "address", name: "recipient", type: "address" },
          { internalType: "uint128", name: "amount0Max", type: "uint128" },
          { internalType: "uint128", name: "amount1Max", type: "uint128" }
        ],
        internalType: "struct INonfungiblePositionManager.CollectParams",
        name: "params",
        type: "tuple"
      }
    ],
    name: "collect",
    outputs: [
      { internalType: "uint256", name: "amount0", type: "uint256" },
      { internalType: "uint256", name: "amount1", type: "uint256" }
    ],
    stateMutability: "payable",
    type: "function"
  }
];
async function revokeAllowance(tokenAddress, spenderAddress, chainId) {
  await switchChain(wagmiConfig, { chainId });
  const hash = await writeContract(wagmiConfig, {
    address: checksumAddress(tokenAddress),
    abi: ERC20_ABI,
    functionName: "approve",
    args: [checksumAddress(spenderAddress), 0n],
    chainId
  });
  const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
  if (receipt.status === "success") {
    return hash;
  } else {
    throw new Error(`Transaction failed: ${hash}`);
  }
}
async function revokeClaimApproval(votingEscrowAddress, voterAddress, chainId = 56) {
  await switchChain(wagmiConfig, { chainId });
  const account = getAccount(wagmiConfig);
  if (!account.address) {
    throw new Error("No wallet connected");
  }
  const hash = await writeContract(wagmiConfig, {
    address: checksumAddress(votingEscrowAddress),
    abi: VOTING_ESCROW_ABI,
    functionName: "setApprovalForAll",
    args: [checksumAddress(voterAddress), false],
    chainId
  });
  const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
  if (receipt.status === "success") {
    return hash;
  }
  throw new Error(`Transaction failed: ${hash}`);
}
async function customSwap(tokenIn, tokenOut, tickSpacing, amountIn, chainId = 97, slippagePercent = 1) {
  const SWAP_ROUTER = "0x3bC81163194D755ceCc87001d29b67fFf6cd4239";
  await switchChain(wagmiConfig, { chainId });
  const account = getAccount(wagmiConfig);
  if (!account.address) {
    throw new Error("No wallet connected");
  }
  const amountInWei = BigInt(parseFloat(amountIn) * 10 ** 18);
  const slippageBps = Math.round(slippagePercent * 100);
  const amountOutMinimum = amountInWei * BigInt(1e4 - slippageBps) / 10000n;
  const deadline = BigInt(Math.floor(Date.now() / 1e3) + 1200);
  const hash = await writeContract(wagmiConfig, {
    address: SWAP_ROUTER,
    abi: SWAP_ROUTER_ABI,
    functionName: "exactInputSingle",
    args: [{
      tokenIn,
      tokenOut,
      tickSpacing,
      recipient: account.address,
      deadline,
      amountIn: amountInWei,
      amountOutMinimum,
      sqrtPriceLimitX96: 0n
      // Protection comes from amountOutMinimum
    }],
    chainId
  });
  const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
  if (receipt.status === "success") {
    return hash;
  } else {
    throw new Error(`Transaction failed: ${hash}`);
  }
}
async function addLiquidity(token0, token1, tickSpacing, amount0, amount1, tickLower = -887200, tickUpper = 887200, chainId = 97) {
  const NFT_POSITION_MANAGER = "0x4C725F23d5F95E72880B483F00d4c9324973800a";
  await switchChain(wagmiConfig, { chainId });
  const account = getAccount(wagmiConfig);
  if (!account.address) {
    throw new Error("No wallet connected");
  }
  const amount0Wei = BigInt(parseFloat(amount0) * 10 ** 18);
  const amount1Wei = BigInt(parseFloat(amount1) * 10 ** 18);
  const amount0Min = amount0Wei * 95n / 100n;
  const amount1Min = amount1Wei * 95n / 100n;
  const deadline = BigInt(Math.floor(Date.now() / 1e3) + 1200);
  const MIN_TICK = -887272;
  const MAX_TICK = 887272;
  const alignTick = (tick, spacing) => {
    return Math.round(tick / spacing) * spacing;
  };
  let alignedTickLower = alignTick(tickLower, tickSpacing);
  let alignedTickUpper = alignTick(tickUpper, tickSpacing);
  alignedTickLower = Math.max(alignedTickLower, MIN_TICK);
  alignedTickUpper = Math.min(alignedTickUpper, MAX_TICK);
  if (alignedTickLower >= alignedTickUpper) {
    alignedTickLower = alignTick(-8e5, tickSpacing);
    alignedTickUpper = alignTick(8e5, tickSpacing);
  }
  const sortedToken0 = token0.toLowerCase() < token1.toLowerCase() ? token0 : token1;
  const sortedToken1 = token0.toLowerCase() < token1.toLowerCase() ? token1 : token0;
  const sortedAmount0 = token0.toLowerCase() < token1.toLowerCase() ? amount0Wei : amount1Wei;
  const sortedAmount1 = token0.toLowerCase() < token1.toLowerCase() ? amount1Wei : amount0Wei;
  const sortedAmount0Min = token0.toLowerCase() < token1.toLowerCase() ? amount0Min : amount1Min;
  const sortedAmount1Min = token0.toLowerCase() < token1.toLowerCase() ? amount1Min : amount0Min;
  const hash = await writeContract(wagmiConfig, {
    address: NFT_POSITION_MANAGER,
    abi: NFT_POSITION_MANAGER_ABI,
    functionName: "mint",
    args: [{
      token0: sortedToken0,
      token1: sortedToken1,
      tickSpacing,
      tickLower: alignedTickLower,
      tickUpper: alignedTickUpper,
      amount0Desired: sortedAmount0,
      amount1Desired: sortedAmount1,
      amount0Min: sortedAmount0Min,
      amount1Min: sortedAmount1Min,
      recipient: account.address,
      deadline,
      sqrtPriceX96: 0n
      // Pool already exists, so set to 0
    }],
    chainId
  });
  const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
  if (receipt.status === "success") {
    return hash;
  } else {
    throw new Error(`Transaction failed: ${hash}`);
  }
}
async function collectV3Lp(tokenId, chainId = 97) {
  const NFT_POSITION_MANAGER = "0x4C725F23d5F95E72880B483F00d4c9324973800a";
  await switchChain(wagmiConfig, { chainId });
  const account = getAccount(wagmiConfig);
  if (!account.address) {
    throw new Error("No wallet connected");
  }
  const MAX_UINT128 = 340282366920938463463374607431768211455n;
  const hash = await writeContract(wagmiConfig, {
    address: NFT_POSITION_MANAGER,
    abi: NFT_POSITION_MANAGER_ABI,
    functionName: "collect",
    args: [{
      tokenId: BigInt(tokenId),
      recipient: account.address,
      amount0Max: MAX_UINT128,
      amount1Max: MAX_UINT128
    }],
    chainId
  });
  const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
  if (receipt.status === "success") {
    return {
      hash,
      amount0: "Check transaction logs",
      amount1: "Check transaction logs"
    };
  } else {
    throw new Error(`Transaction failed: ${hash}`);
  }
}
export function useWindowFunctions() {
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.revokeAllowance = revokeAllowance;
      window.revokeClaimApproval = revokeClaimApproval;
      window.customSwap = customSwap;
      window.addLiquidity = addLiquidity;
      window.collectV3Lp = collectV3Lp;
    }
  }, []);
}
